import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Send, Bot, User, ArrowRight, Phone, MessageSquare, Shield, Home } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_COLORS = {
  buyer_inquiry: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  seller_inquiry: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  listing_question: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  showing_request: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  appointment_request: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  callback_request: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  direct_agent_request: 'bg-red-500/10 text-red-400 border-red-500/20',
  upset_caller: 'bg-red-500/10 text-red-400 border-red-500/20',
  general_question: 'bg-secondary text-muted-foreground border-border',
  spam_or_unknown: 'bg-secondary text-muted-foreground border-border/20',
};

export default function CallTestPage() {
  const { token } = useAuth();
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const handleTest = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setLoading(true);

    const userMsg = message.trim();
    setMessage('');

    try {
      const { data } = await axios.post(`${API}/calls/test`, { message: userMsg }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResults((prev) => [...prev, { input: userMsg, ...data }]);
    } catch (err) {
      toast.error('Failed to get AI response');
    } finally {
      setLoading(false);
    }
  };

  const quickTests = [
    'Find homes under $700K in Elk Grove',
    'Show 4 bedroom homes in Sacramento',
    'How many homes are for sale in 95758?',
    'Find homes in Los Angeles under $500K',
    'I want to buy a home in downtown, 3 bed, budget $500K',
    'I need to sell my house urgently',
    'Can I schedule a showing this weekend?',
    'I want to speak with the agent directly',
  ];

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto space-y-6" data-testid="call-test-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Call Test</h1>
        <p className="text-sm text-muted-foreground mt-1">Preview how your AI assistant handles caller messages</p>
      </div>

      {/* Quick test buttons */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-sm">Quick Tests</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {quickTests.map((q, i) => (
            <button
              key={i}
              onClick={() => setMessage(q)}
              className="text-xs bg-muted text-foreground hover:bg-accent/60 px-3 py-1.5 rounded-full transition-colors"
              data-testid={`quick-test-${i}`}
            >
              {q.length > 45 ? q.slice(0, 45) + '...' : q}
            </button>
          ))}
        </CardContent>
      </Card>

      {/* Input */}
      <form onSubmit={handleTest} className="flex gap-2">
        <Input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type a caller message to test..."
          className="flex-1 bg-muted border-border text-foreground placeholder:text-muted-foreground"
          disabled={loading}
          data-testid="call-test-input"
        />
        <Button type="submit" disabled={loading || !message.trim()} className="bg-emerald-500 hover:bg-emerald-600 text-white shrink-0" data-testid="call-test-submit">
          {loading ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Send size={16} />}
        </Button>
      </form>

      {/* Results */}
      <div className="space-y-4">
        {results.map((r, i) => (
          <Card key={r.timestamp || i} className="border-border bg-card" data-testid={`call-test-result-${i}`}>
            <CardContent className="p-4 space-y-4">
              {/* Caller message */}
              <div className="flex items-start gap-3">
                <div className="h-7 w-7 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0">
                  <User size={14} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Caller</p>
                  <p className="text-sm text-foreground">{r.input}</p>
                </div>
              </div>

              {/* AI response */}
              <div className="flex items-start gap-3">
                <div className="h-7 w-7 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <Bot size={14} className="text-emerald-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-0.5">AI Assistant</p>
                  <p className="text-sm text-foreground">{r.response_text}</p>
                </div>
              </div>

              {/* Analysis */}
              <div className="bg-muted rounded-lg p-3 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={`text-xs ${INTENT_COLORS[r.detected_intent] || INTENT_COLORS.general_question}`}>
                    {r.detected_intent?.replace(/_/g, ' ')}
                  </Badge>
                  <Badge className={`text-xs ${r.should_transfer ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-accent/50 text-muted-foreground border-border'}`}>
                    <Phone size={10} className="mr-1" />
                    {r.should_transfer ? 'Transfer' : 'Continue'}
                  </Badge>
                  {r.should_send_sms_followup && (
                    <Badge className="text-xs bg-violet-500/10 text-violet-400 border-violet-500/20">
                      <MessageSquare size={10} className="mr-1" /> SMS Follow-up
                    </Badge>
                  )}
                  <Badge className="text-xs bg-accent/50 text-muted-foreground border-border">
                    Confidence: {Math.round((r.confidence || 0) * 100)}%
                  </Badge>
                </div>

                {/* Entities */}
                {r.extracted_entities && Object.keys(r.extracted_entities).length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Extracted Entities</p>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(r.extracted_entities).map(([k, v]) => (
                        <span key={k} className="text-xs bg-accent/50 text-foreground px-2 py-0.5 rounded">
                          {k}: {String(v)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Transfer Decision */}
                {r.transfer_decision && (
                  <div className="flex items-center gap-2">
                    <ArrowRight size={12} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      Transfer: {r.transfer_decision.transfer ? 'Yes' : 'No'}
                      {r.transfer_decision.reason && ` — ${r.transfer_decision.reason}`}
                    </span>
                  </div>
                )}

                {r.mock && (
                  <div className="flex items-center gap-1">
                    <Shield size={10} className="text-amber-500" />
                    <span className="text-xs text-amber-500">Mock mode (OpenAI not connected)</span>
                  </div>
                )}
              </div>

              {/* Property Results */}
              {r.property_results && r.property_results.results?.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      Property Results ({r.property_results.total} found)
                    </p>
                    <Badge className={`text-xs ${r.property_results.live ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}`}>
                      {r.property_results.provider === 'mock' ? 'Sample Data' : 'Live Search'}
                    </Badge>
                  </div>
                  {r.property_results.results.slice(0, 5).map((listing, j) => (
                    <div key={j} className="flex items-start gap-3 bg-muted rounded-lg p-3" data-testid={`listing-result-${j}`}>
                      {listing.photo_url ? (
                        <img src={listing.photo_url} alt="" className="h-14 w-20 rounded object-cover shrink-0 bg-accent" />
                      ) : (
                        <div className="h-14 w-20 rounded bg-accent flex items-center justify-center shrink-0">
                          <Home size={16} className="text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {listing.address_line || 'Address N/A'}, {listing.city}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {listing.beds} bed, {listing.baths} bath
                          {listing.sqft ? `, ${listing.sqft.toLocaleString()} sqft` : ''}
                          {listing.property_type ? ` — ${listing.property_type.replace(/_/g, ' ')}` : ''}
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-emerald-400 shrink-0">
                        {listing.price ? `$${listing.price.toLocaleString()}` : 'N/A'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {results.length === 0 && (
        <div className="text-center py-12">
          <Bot className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Enter a caller message above to see how your AI responds</p>
        </div>
      )}
    </div>
  );
}
